import React, { useState, useEffect, useCallback, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, type ActionButton } from 'components/modals';
import { SyutSelect, type SelectOption } from 'components/ui';
import { EnterpriseScreenShell, actionButtonFactory, useCan } from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import {
  entityAccountingConfigService, coaLedgerService, coaGroupService, accountSubtypeService,
  currencyService,
} from 'api/services';
import type { EntityAccountingConfigDto, LedgerDefaultDto, LedgerPurposeDto } from 'api/types';

type SectionId = 'currency' | 'receivables' | 'payables' | 'ledgers';
const SECTIONS: { id: SectionId; label: string; icon: keyof typeof IconSystem; desc: string }[] = [
  { id: 'currency', label: 'Functional Currency', icon: 'dollarSign', desc: 'The currency the books are kept in' },
  { id: 'receivables', label: 'Receivables', icon: 'users', desc: 'Where customer ledgers are auto-created' },
  { id: 'payables', label: 'Payables', icon: 'users', desc: 'Where supplier ledgers are auto-created' },
  { id: 'ledgers', label: 'Default Ledgers', icon: 'list', desc: 'System posting ledgers per purpose' },
];

const SettingRow: React.FC<{ label: React.ReactNode; description?: string; children: React.ReactNode }> = ({ label, description, children }) => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1.5rem',
    padding: '0.9rem 0', borderBottom: '1px solid var(--border-color)', flexWrap: 'wrap',
  }}>
    <div style={{ minWidth: 220, flex: 1 }}>
      <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)' }}>{label}</div>
      {description && <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>{description}</div>}
    </div>
    <div style={{ minWidth: 300, maxWidth: 380, width: 340 }}>{children}</div>
  </div>
);

interface EntityAccountingConfigScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const EntityAccountingConfigScreen: React.FC<EntityAccountingConfigScreenProps> = ({ moduleIcon, tabId }) => {
  const can = useCan('entity-accounting-config');
  const canEdit = can('UPDATE');

  const [activeSection, setActiveSection] = useState<SectionId>('currency');

  const [config, setConfig] = useState<EntityAccountingConfigDto | null>(null);
  const [currency, setCurrency] = useState('');
  const [savingCurrency, setSavingCurrency] = useState(false);
  const currencyLabelCache = useRef<Map<string, string>>(new Map());

  const [receivablesSubtypeId, setReceivablesSubtypeId] = useState('');
  const [receivablesGroupId, setReceivablesGroupId] = useState('');
  const [savingReceivables, setSavingReceivables] = useState(false);
  // ADR-0040 mirror — where supplier AP ledgers are auto-created (subtype required: PURCHASING.PAYABLES_SUBTYPE; group optional).
  const [payablesSubtypeId, setPayablesSubtypeId] = useState('');
  const [payablesGroupId, setPayablesGroupId] = useState('');
  const [savingPayables, setSavingPayables] = useState(false);
  const subtypeCache = useRef<Map<string, string>>(new Map());
  const groupCache = useRef<Map<string, string>>(new Map());

  const [purposes, setPurposes] = useState<LedgerPurposeDto[]>([]);
  const [defaults, setDefaults] = useState<LedgerDefaultDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyPurpose, setBusyPurpose] = useState<string | null>(null);

  // ADR-0026 ③b — balanced branch posting toggle.
  const [balancedBuPosting, setBalancedBuPosting] = useState(false);
  const [savingBalanced, setSavingBalanced] = useState(false);
  const [balancedConfirmOpen, setBalancedConfirmOpen] = useState(false);

  const [currencyConfirmOpen, setCurrencyConfirmOpen] = useState(false);
  const [receivablesConfirmOpen, setReceivablesConfirmOpen] = useState(false);
  const [payablesConfirmOpen, setPayablesConfirmOpen] = useState(false);
  const [pendingLedgerChange, setPendingLedgerChange] = useState<{
    purposeCode: string;
    purposeLabel: string;
    ledgerId: string | null;
  } | null>(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, purp, defs] = await Promise.all([
        entityAccountingConfigService.getConfig(),
        entityAccountingConfigService.getPurposes(),
        entityAccountingConfigService.getLedgerDefaults(),
      ]);
      setConfig(cfg);
      setCurrency(cfg.functionalCurrencyCode || '');
      setReceivablesSubtypeId(cfg.receivablesSubtypeId || '');
      setReceivablesGroupId(cfg.receivablesGroupId || '');
      setBalancedBuPosting(!!cfg.balancedBuPosting);
      if (cfg.receivablesSubtypeId && cfg.receivablesSubtypeName) {
        subtypeCache.current.set(cfg.receivablesSubtypeId, cfg.receivablesSubtypeName);
      }
      if (cfg.receivablesGroupId && cfg.receivablesGroupName) {
        groupCache.current.set(cfg.receivablesGroupId, cfg.receivablesGroupName);
      }
      setPayablesSubtypeId(cfg.payablesSubtypeId || '');
      setPayablesGroupId(cfg.payablesGroupId || '');
      if (cfg.payablesSubtypeId && cfg.payablesSubtypeName) {
        subtypeCache.current.set(cfg.payablesSubtypeId, cfg.payablesSubtypeName);
      }
      if (cfg.payablesGroupId && cfg.payablesGroupName) {
        groupCache.current.set(cfg.payablesGroupId, cfg.payablesGroupName);
      }
      setPurposes(purp);
      setDefaults(defs);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not load accounting configuration.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Concurrency-safe helper: refetches config so a stale rowVersion (bumped by
  // another screen — e.g. TaxSettingsScreen saving a jurisdiction) doesn't
  // permanently block a save on THIS screen. On mismatch we replace `config`
  // (rowVersion + backend truth for jurisdictionCode + hasTransactions) but
  // preserve the user's local edits (currency, receivables picks) so they can
  // review and retry.
  const refreshConfigFromServer = useCallback(async () => {
    const fresh = await entityAccountingConfigService.getConfig();
    setConfig(fresh);
    return fresh;
  }, []);

  const persist = useCallback(async (patch: Partial<{ currency: string; confirmCurrency: boolean; subtypeId: string; groupId: string; paySubtypeId: string; payGroupId: string; balanced: boolean }>) => {
    const buildBody = (source: typeof config) => ({
      functionalCurrencyCode: patch.currency ?? source?.functionalCurrencyCode ?? currency,
      jurisdictionCode: source?.jurisdictionCode ?? null,
      receivablesSubtypeId: (patch.subtypeId ?? receivablesSubtypeId) || null,
      receivablesGroupId: (patch.groupId ?? receivablesGroupId) || null,
      payablesSubtypeId: (patch.paySubtypeId ?? payablesSubtypeId) || null,
      payablesGroupId: (patch.payGroupId ?? payablesGroupId) || null,
      // Always carried — a currency/receivables save must not reset the toggle.
      balancedBuPosting: patch.balanced ?? source?.balancedBuPosting ?? balancedBuPosting,
      confirmFunctionalCurrency: !!patch.confirmCurrency,
      rowVersion: source?.rowVersion ?? 0,
    });
    try {
      const saved = await entityAccountingConfigService.saveConfig(buildBody(config));
      setConfig(saved);
      return saved;
    } catch (err: any) {
      const msg: string = err?.response?.data?.message || err?.message || '';
      if (/modified by another user/i.test(msg)) {
        const fresh = await refreshConfigFromServer();
        const saved = await entityAccountingConfigService.saveConfig(buildBody(fresh));
        setConfig(saved);
        return saved;
      }
      throw err;
    }
  }, [config, currency, receivablesSubtypeId, receivablesGroupId, payablesSubtypeId, payablesGroupId, balancedBuPosting, refreshConfigFromServer]);

  const currencyDirty = !!config && currency !== config.functionalCurrencyCode;
  const currencyLocked = !!config?.hasTransactions;
  // A default currency nobody has confirmed yet (Setup Checklist warning) can be confirmed
  // unchanged — even after postings, since confirming does not change it.
  const currencyUnconfirmed = !!config?.id && !!config.functionalCurrencyCode && !config.functionalCurrencyConfirmedAt;
  const currencyConfirmOnly = currencyUnconfirmed && !currencyDirty;
  const handleSaveCurrency = useCallback(() => {
    setCurrencyConfirmOpen(true);
  }, []);
  const handleConfirmSaveCurrency = useCallback(async () => {
    setSavingCurrency(true);
    try {
      const saved = await persist({ currency, confirmCurrency: true });
      setCurrency(saved.functionalCurrencyCode);
      setSavingCurrency(false);
      setCurrencyConfirmOpen(false);
      notificationService.toast.success(currencyConfirmOnly ? 'Functional currency confirmed.' : 'Functional currency saved.');
    } catch (err: any) {
      setSavingCurrency(false);
      notificationService.toast.error(err?.response?.data?.message || 'Could not save functional currency.');
    }
  }, [currency, currencyConfirmOnly, persist]);

  // ADR-0026 ③b — balanced branch posting save flow.
  const balancedDirty = !!config && balancedBuPosting !== !!config.balancedBuPosting;
  const handleConfirmSaveBalanced = useCallback(async () => {
    setSavingBalanced(true);
    try {
      const saved = await persist({ balanced: balancedBuPosting });
      setBalancedBuPosting(!!saved.balancedBuPosting);
      setSavingBalanced(false);
      setBalancedConfirmOpen(false);
      notificationService.toast.success(`Balanced branch posting ${saved.balancedBuPosting ? 'enabled' : 'disabled'}.`);
    } catch (err: any) {
      setSavingBalanced(false);
      setBalancedConfirmOpen(false);
      // Server validates the INTER_BU_CONTROL default mapping — surface its message.
      notificationService.toast.error(err?.response?.data?.message || 'Could not save the balanced branch posting setting.');
    }
  }, [balancedBuPosting, persist]);

  const receivablesDirty = !!config && (
    (receivablesSubtypeId || '') !== (config.receivablesSubtypeId || '')
    || (receivablesGroupId || '') !== (config.receivablesGroupId || '')
  );
  const handleSaveReceivables = useCallback(() => {
    setReceivablesConfirmOpen(true);
  }, []);
  const handleConfirmSaveReceivables = useCallback(async () => {
    setSavingReceivables(true);
    try {
      const saved = await persist({ subtypeId: receivablesSubtypeId, groupId: receivablesGroupId });
      setReceivablesSubtypeId(saved.receivablesSubtypeId || '');
      setReceivablesGroupId(saved.receivablesGroupId || '');
      setSavingReceivables(false);
      setReceivablesConfirmOpen(false);
      notificationService.toast.success('Receivables configuration saved.');
    } catch (err: any) {
      setSavingReceivables(false);
      notificationService.toast.error(err?.response?.data?.message || 'Could not save the receivables configuration.');
    }
  }, [receivablesSubtypeId, receivablesGroupId, persist]);

  const payablesDirty = !!config && (
    (payablesSubtypeId || '') !== (config.payablesSubtypeId || '')
    || (payablesGroupId || '') !== (config.payablesGroupId || '')
  );
  const handleSavePayables = useCallback(() => {
    setPayablesConfirmOpen(true);
  }, []);
  const handleConfirmSavePayables = useCallback(async () => {
    setSavingPayables(true);
    try {
      const saved = await persist({ paySubtypeId: payablesSubtypeId, payGroupId: payablesGroupId });
      setPayablesSubtypeId(saved.payablesSubtypeId || '');
      setPayablesGroupId(saved.payablesGroupId || '');
      setSavingPayables(false);
      setPayablesConfirmOpen(false);
      notificationService.toast.success('Payables configuration saved.');
    } catch (err: any) {
      setSavingPayables(false);
      notificationService.toast.error(err?.response?.data?.message || 'Could not save the payables configuration.');
    }
  }, [payablesSubtypeId, payablesGroupId, persist]);

  const loadCurrencyOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const list = await currencyService.getAll(input || undefined);
      list.forEach(c => currencyLabelCache.current.set(c.currencyCode, `${c.currencyCode} — ${c.currencyName}`));
      return list.map(c => ({ value: c.currencyCode, label: `${c.currencyCode} — ${c.currencyName}` }));
    } catch {
      return [];
    }
  }, []);

  const loadSubtypeOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await accountSubtypeService.getAll({ pageSize: 50, searchTerm: input || undefined });
      res.data.forEach(s => subtypeCache.current.set(s.id, `${s.subtypeCode} - ${s.subtypeName}`));
      return res.data.map(s => ({ value: s.id, label: `${s.subtypeCode} - ${s.subtypeName}` }));
    } catch {
      return [];
    }
  }, []);
  const loadGroupOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await coaGroupService.getAll({
        accountSubtypeId: receivablesSubtypeId || undefined,
        pageSize: 50, searchTerm: input || undefined,
      });
      res.data.forEach(g => groupCache.current.set(g.id, `${g.groupCode} - ${g.groupName}`));
      return res.data.map(g => ({ value: g.id, label: `${g.groupCode} - ${g.groupName}` }));
    } catch {
      return [];
    }
  }, [receivablesSubtypeId]);
  const loadPayablesGroupOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await coaGroupService.getAll({
        accountSubtypeId: payablesSubtypeId || undefined,
        pageSize: 50, searchTerm: input || undefined,
      });
      res.data.forEach(g => groupCache.current.set(g.id, `${g.groupCode} - ${g.groupName}`));
      return res.data.map(g => ({ value: g.id, label: `${g.groupCode} - ${g.groupName}` }));
    } catch {
      return [];
    }
  }, [payablesSubtypeId]);
  const loadLedgerOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await coaLedgerService.getAll({
        ledgerKind: 'GL', pageSize: 50, searchTerm: input || undefined,
      });
      return res.data.map(l => ({ value: l.id, label: `${l.ledgerCode} - ${l.ledgerName}` }));
    } catch {
      return [];
    }
  }, []);

  const defaultFor = (purposeCode: string) => defaults.find(d => d.purposeCode === purposeCode);
  const handleSetDefault = useCallback((purposeCode: string, purposeLabel: string, ledgerId: string | null) => {
    setPendingLedgerChange({ purposeCode, purposeLabel, ledgerId });
  }, []);
  const handleConfirmSetDefault = useCallback(async () => {
    if (!pendingLedgerChange) return;
    const { purposeCode, ledgerId } = pendingLedgerChange;
    setBusyPurpose(purposeCode);
    try {
      if (ledgerId) {
        const saved = await entityAccountingConfigService.setLedgerDefault({ purposeCode, ledgerId });
        setDefaults(prev => [...prev.filter(d => d.purposeCode !== purposeCode), saved]);
        setBusyPurpose(null);
        setPendingLedgerChange(null);
        notificationService.toast.success('Default ledger updated.');
      } else {
        await entityAccountingConfigService.clearLedgerDefault(purposeCode);
        setDefaults(prev => prev.filter(d => d.purposeCode !== purposeCode));
        setBusyPurpose(null);
        setPendingLedgerChange(null);
        notificationService.toast.success('Default ledger cleared.');
      }
    } catch (err: any) {
      setBusyPurpose(null);
      notificationService.toast.error(err?.response?.data?.message || 'Could not update the default ledger.');
    }
  }, [pendingLedgerChange]);

  const actionButtons: ActionButton[] = [
    actionButtonFactory.refresh(loadAll),
  ];

  const sectionMeta = SECTIONS.find(s => s.id === activeSection)!;

  return (
    <EnterpriseScreenShell
      title="Entity Accounting Config"
      description="Functional currency, receivables, payables, and the default posting ledgers for this entity"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.settings size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
    >
      <div style={{
        flex: 1, display: 'flex', overflow: 'hidden',
        border: '1px solid var(--border-color)', borderRadius: '0.75rem',
        background: 'var(--bg-primary)', minHeight: 0,
      }}>
        {/* Left nav */}
        <div style={{
          width: 240, flexShrink: 0, borderRight: '1px solid var(--border-color)',
          padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.25rem', overflow: 'auto',
        }}>
          {SECTIONS.map(s => {
            const Icon = IconSystem[s.icon] as React.FC<{ size?: number }>;
            const active = activeSection === s.id;
            return (
              <div key={s.id} role="button" tabIndex={0}
                onClick={() => setActiveSection(s.id)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveSection(s.id); } }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.6rem',
                  padding: '0.6rem 0.75rem', borderRadius: '0.5rem', cursor: 'pointer', userSelect: 'none',
                  borderLeft: active ? '3px solid var(--primary)' : '3px solid transparent',
                  background: active ? 'var(--bg-secondary)' : 'transparent',
                  color: active ? 'var(--primary)' : 'var(--text-primary)',
                  fontWeight: active ? 600 : 500,
                }}>
                {Icon && <Icon size={18} />}
                <span style={{ fontSize: '0.875rem' }}>{s.label}</span>
              </div>
            );
          })}
        </div>

        {/* Right content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '1.5rem 1.75rem', minWidth: 0 }}>
          <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-primary)' }}>
            {sectionMeta.label}
          </h2>
          <p style={{ margin: '0 0 1.25rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
            {sectionMeta.desc}
          </p>

          {activeSection === 'currency' && (
            <>
              <SettingRow label="Functional Currency"
                description="The currency the books are kept in. Ledgers in any other currency are treated as foreign.">
                <SyutSelect isAsync loadOptions={loadCurrencyOptions}
                  defaultOptions cacheOptions={false}
                  seedOptions={currency
                    ? [{ value: currency, label: currencyLabelCache.current.get(currency) || currency }]
                    : undefined}
                  menuPortalTarget={document.body}
                  value={currency} onChange={(v) => setCurrency((v as string) || '')}
                  isClearable={false} isDisabled={currencyLocked || !canEdit} />
              </SettingRow>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '1rem' }}>
                <button className="btn btn-primary" onClick={handleSaveCurrency}
                  disabled={currencyConfirmOnly
                    ? savingCurrency || !canEdit
                    : !currencyDirty || savingCurrency || currencyLocked || !canEdit}>
                  {savingCurrency ? 'Saving…' : currencyConfirmOnly ? 'Confirm' : 'Save'}
                </button>
                {currencyConfirmOnly && (
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    Not confirmed yet — {currency} was set by default. Confirm it, or choose another currency.
                  </span>
                )}
                {currencyLocked && !currencyConfirmOnly && (
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    Locked — this entity already has postings, so the functional currency can't be changed.
                  </span>
                )}
              </div>
              {config?.updatedAt && (
                <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  Last updated {new Date(config.updatedAt).toLocaleString()}
                  {config.updatedByName ? ` by ${config.updatedByName}` : ''}.
                </div>
              )}
            </>
          )}

          {activeSection === 'receivables' && (
            <>
              <SettingRow label={<>Receivables Account Subtype <span className="text-danger">*</span></>}
                description="The account subtype customer ledgers are classified under (e.g. Trade Receivables). Required before posting Service Invoices.">
                <SyutSelect isAsync loadOptions={loadSubtypeOptions} defaultOptions cacheOptions={false}
                  seedOptions={receivablesSubtypeId
                    ? [{ value: receivablesSubtypeId, label: subtypeCache.current.get(receivablesSubtypeId) || '' }]
                    : undefined}
                  menuPortalTarget={document.body} value={receivablesSubtypeId}
                  onChange={(v) => { const id = (v as string) || ''; setReceivablesSubtypeId(id); setReceivablesGroupId(''); }}
                  placeholder="e.g. Trade Receivables / Sundry Debtors"
                  isClearable isDisabled={!canEdit} />
              </SettingRow>
              <SettingRow label="Receivables Group"
                description="Optional — the Chart-of-Accounts group customer ledgers sit under. Leave empty to place them directly under the subtype.">
                <SyutSelect key={`grp-${receivablesSubtypeId}`} isAsync loadOptions={loadGroupOptions}
                  defaultOptions cacheOptions={false}
                  seedOptions={receivablesGroupId
                    ? [{ value: receivablesGroupId, label: groupCache.current.get(receivablesGroupId) || '' }]
                    : undefined}
                  menuPortalTarget={document.body} value={receivablesGroupId}
                  onChange={(v) => setReceivablesGroupId((v as string) || '')}
                  placeholder={receivablesSubtypeId ? 'None (directly under subtype)' : 'Pick a subtype first'}
                  isClearable isDisabled={!receivablesSubtypeId || !canEdit} />
              </SettingRow>
              <div style={{ marginTop: '1rem' }}>
                <button className="btn btn-primary" onClick={handleSaveReceivables}
                  disabled={!receivablesDirty || savingReceivables || !canEdit}>
                  {savingReceivables ? 'Saving…' : 'Save'}
                </button>
              </div>
            </>
          )}

          {activeSection === 'payables' && (
            <>
              <SettingRow label={<>Payables Account Subtype <span className="text-danger">*</span></>}
                description="The account subtype supplier ledgers are classified under (e.g. Trade Payables / Sundry Creditors). Required before posting Purchase Invoices (ADR-0040/0049).">
                <SyutSelect isAsync loadOptions={loadSubtypeOptions} defaultOptions cacheOptions={false}
                  seedOptions={payablesSubtypeId
                    ? [{ value: payablesSubtypeId, label: subtypeCache.current.get(payablesSubtypeId) || '' }]
                    : undefined}
                  menuPortalTarget={document.body} value={payablesSubtypeId}
                  onChange={(v) => { const id = (v as string) || ''; setPayablesSubtypeId(id); setPayablesGroupId(''); }}
                  placeholder="e.g. Trade Payables / Sundry Creditors"
                  isClearable isDisabled={!canEdit} />
              </SettingRow>
              <SettingRow label="Payables Group"
                description="Optional — the Chart-of-Accounts group supplier ledgers sit under. Leave empty to place them directly under the subtype. A supplier's ledger is created on their first bill.">
                <SyutSelect key={`pgrp-${payablesSubtypeId}`} isAsync loadOptions={loadPayablesGroupOptions}
                  defaultOptions cacheOptions={false}
                  seedOptions={payablesGroupId
                    ? [{ value: payablesGroupId, label: groupCache.current.get(payablesGroupId) || '' }]
                    : undefined}
                  menuPortalTarget={document.body} value={payablesGroupId}
                  onChange={(v) => setPayablesGroupId((v as string) || '')}
                  placeholder={payablesSubtypeId ? 'Pick the payables group' : 'Pick a subtype first'}
                  isClearable isDisabled={!payablesSubtypeId || !canEdit} />
              </SettingRow>
              <div style={{ marginTop: '1rem' }}>
                <button className="btn btn-primary" onClick={handleSavePayables}
                  disabled={!payablesDirty || savingPayables || !canEdit}>
                  {savingPayables ? 'Saving…' : 'Save'}
                </button>
              </div>
            </>
          )}

          {activeSection === 'ledgers' && (
            <>
              <p style={{ margin: '-0.75rem 0 1rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                The ledger the posting engine uses for each system purpose. Pick a ledger per purpose, or clear it.
                Sales/Purchase/Tax defaults are configured in their own modules.
              </p>

              {/* ADR-0026 ③b — balanced branch posting */}
              <SettingRow label="Balanced branch posting"
                description="The engine adds Inter-BU Control contra lines to cross-branch vouchers so every branch's books self-balance. Requires the 'Inter-BU / Branch Control' ledger default to be mapped below. Enable at a clean cutoff (ideally the start of a financial year).">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: canEdit ? 'pointer' : 'default', fontSize: '0.85rem' }}>
                    <input type="checkbox" checked={balancedBuPosting} disabled={!canEdit || savingBalanced}
                      onChange={(e) => setBalancedBuPosting(e.target.checked)} />
                    {balancedBuPosting ? 'Enabled' : 'Disabled'}
                  </label>
                  <button className="btn btn-primary" onClick={() => setBalancedConfirmOpen(true)}
                    disabled={!balancedDirty || savingBalanced || !canEdit}
                    style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}>
                    {savingBalanced ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </SettingRow>
              {loading && <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Loading…</div>}
              {!loading && purposes.length === 0 && (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>No purposes configured.</div>
              )}
              {!loading && purposes.map((p) => {
                const current = defaultFor(p.value);
                const seed = current
                  ? [{ value: current.ledgerId, label: `${current.ledgerCode ?? ''} - ${current.ledgerName ?? ''}` }]
                  : undefined;
                return (
                  <SettingRow key={p.value} label={p.label}>
                    <SyutSelect key={`${p.value}-${current?.ledgerId ?? 'none'}`}
                      isAsync loadOptions={loadLedgerOptions} defaultOptions cacheOptions={false}
                      seedOptions={seed} menuPortalTarget={document.body}
                      value={current?.ledgerId ?? ''}
                      onChange={(v) => handleSetDefault(p.value, p.label, (v as string) || null)}
                      placeholder="Not set — pick a ledger" isClearable size="sm"
                      isDisabled={busyPurpose === p.value || !canEdit} />
                  </SettingRow>
                );
              })}
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={currencyConfirmOpen}
        title={currencyConfirmOnly ? 'Confirm functional currency?' : 'Save functional currency?'}
        message={currencyConfirmOnly
          ? `Confirm "${currency}" as the currency this entity's books are kept in? Once transactions are posted, the functional currency cannot be changed.`
          : `Save "${currency}" as the functional currency for this entity? Once transactions are posted, the functional currency cannot be changed.`}
        confirmLabel={currencyConfirmOnly ? 'Confirm' : 'Save'}
        cancelLabel="Cancel"
        variant="default"
        loading={savingCurrency}
        loadingMessage="Saving..."
        onConfirm={handleConfirmSaveCurrency}
        onCancel={() => setCurrencyConfirmOpen(false)}
      />

      <ConfirmDialog
        open={receivablesConfirmOpen}
        title="Save receivables configuration?"
        message="Save the receivables account subtype and group? New customer ledgers will be auto-created under this classification."
        confirmLabel="Save"
        cancelLabel="Cancel"
        variant="default"
        loading={savingReceivables}
        loadingMessage="Saving..."
        onConfirm={handleConfirmSaveReceivables}
        onCancel={() => setReceivablesConfirmOpen(false)}
      />

      <ConfirmDialog
        open={payablesConfirmOpen}
        title="Save payables configuration?"
        message="Save the payables account subtype and group? New supplier ledgers will be auto-created under this classification on their first bill."
        confirmLabel="Save"
        cancelLabel="Cancel"
        variant="default"
        loading={savingPayables}
        loadingMessage="Saving..."
        onConfirm={handleConfirmSavePayables}
        onCancel={() => setPayablesConfirmOpen(false)}
      />

      <ConfirmDialog
        open={balancedConfirmOpen}
        title={balancedBuPosting ? 'Enable balanced branch posting?' : 'Disable balanced branch posting?'}
        message={balancedBuPosting
          ? "Enable balanced branch posting? The engine will add Inter-BU Control contra lines to every cross-branch voucher from now on. The 'Inter-BU / Branch Control' ledger default must be mapped. Best enabled at a clean cutoff (ideally the start of a financial year)."
          : 'Disable balanced branch posting? New cross-branch vouchers will post without Inter-BU Control contra lines; branch books may stop self-balancing from this point.'}
        confirmLabel="Save"
        cancelLabel="Cancel"
        variant={balancedBuPosting ? 'default' : 'danger'}
        loading={savingBalanced}
        loadingMessage="Saving..."
        onConfirm={handleConfirmSaveBalanced}
        onCancel={() => setBalancedConfirmOpen(false)}
      />

      <ConfirmDialog
        open={!!pendingLedgerChange}
        title={pendingLedgerChange?.ledgerId ? 'Update default ledger?' : 'Clear default ledger?'}
        message={pendingLedgerChange?.ledgerId
          ? `Set the default posting ledger for "${pendingLedgerChange.purposeLabel}"? The engine will use this ledger for all future postings of this purpose.`
          : `Clear the default posting ledger for "${pendingLedgerChange?.purposeLabel}"? Postings of this purpose will need an explicit ledger until a new default is set.`}
        confirmLabel={pendingLedgerChange?.ledgerId ? 'Update' : 'Clear'}
        cancelLabel="Cancel"
        variant={pendingLedgerChange?.ledgerId ? 'default' : 'danger'}
        loading={!!pendingLedgerChange && busyPurpose === pendingLedgerChange.purposeCode}
        loadingMessage="Saving..."
        onConfirm={handleConfirmSetDefault}
        onCancel={() => setPendingLedgerChange(null)}
      />
    </EnterpriseScreenShell>
  );
};

export default EntityAccountingConfigScreen;
